import React, { useEffect, useRef, useState } from 'react';
import styles from './DeliveryConfirmation.module.css';

export interface DeliveryConfirmationData {
  quantity?: string;
  notes?: string;
  pod_photo_url: string;      // base64 data URL of the photo
  pod_signature_url: string;  // base64 data URL of the signature
  pod_received_by: string;    // recipient name — required (VP-DRV-004)
}

interface DeliveryConfirmationProps {
  onConfirm?: (data: DeliveryConfirmationData) => void | Promise<void>;
  onCancel?: () => void;
}

/**
 * Driver proof-of-delivery panel.
 *
 * - Photo upload: hidden <input type="file"> triggered by a styled <label>
 *   (same pattern as onboarding Step3DocumentUpload). The picked file is
 *   read into a base64 data URL via FileReader and previewed inline.
 * - Signature: HTML canvas with mouse + touch handlers, exported as a base64
 *   PNG via canvas.toDataURL (same pattern as onboarding Step5ReviewSignature).
 *
 * Both data URLs are sent in pod_photo_url / pod_signature_url on the
 * existing DeliverStopInput payload; the backend stores them in TEXT columns.
 */
export const DeliveryConfirmation: React.FC<DeliveryConfirmationProps> = ({ onConfirm, onCancel }) => {
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [photoDataUrl, setPhotoDataUrl] = useState('');
  const [photoName, setPhotoName] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const sigDirtyRef = useRef(false);

  // Initialize canvas once mounted.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const handlePhoto = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoDataUrl(typeof reader.result === 'string' ? reader.result : '');
      setPhotoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const pointerPos = (canvas: HTMLCanvasElement, clientX: number, clientY: number) => {
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    isDrawingRef.current = true;
    const { x, y } = pointerPos(canvas, clientX, clientY);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (clientX: number, clientY: number) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointerPos(canvas, clientX, clientY);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    sigDirtyRef.current = true;
  };

  const endDraw = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (sigDirtyRef.current) {
      setSignatureDataUrl(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    sigDirtyRef.current = false;
    setSignatureDataUrl('');
  };

  const handleSubmit = async () => {
    if (!receivedBy.trim()) { setError('Recipient name is required'); return; }
    if (!photoDataUrl) { setError('Photo of the delivered item is required'); return; }
    if (!signatureDataUrl) { setError('Signature is required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm?.({
        quantity: quantity.trim() || undefined,
        notes: notes.trim() || undefined,
        pod_photo_url: photoDataUrl,
        pod_signature_url: signatureDataUrl,
        pod_received_by: receivedBy.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete delivery');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Delivery Confirmation</h3>

      <div className={styles.field}>
        <label className={styles.label}>Delivered Quantity</label>
        <input
          className={styles.input}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="e.g. 12 cases"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>
          Recipient Name <span className={styles.required}>*Required</span>
        </label>
        <input
          className={styles.input}
          value={receivedBy}
          onChange={(e) => setReceivedBy(e.target.value)}
          placeholder="Name of person who accepted the delivery"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Delivery Notes (Optional)</label>
        <textarea
          className={styles.textarea}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Photo */}
      <div className={styles.field}>
        <label className={styles.label}>
          Upload Photo <span className={styles.required}>*Required</span>
        </label>
        {photoDataUrl ? (
          <div className={styles.previewBox}>
            <img src={photoDataUrl} alt="Delivery proof" className={styles.previewImg} />
            <div className={styles.previewMeta}>
              <span className={styles.previewName}>{photoName || 'Photo'}</span>
              <button
                type="button"
                className={styles.previewClear}
                onClick={() => { setPhotoDataUrl(''); setPhotoName(''); }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <label className={styles.uploadBox}>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className={styles.hiddenInput}
              onChange={(e) => handlePhoto(e.target.files?.[0] || null)}
            />
            <span className={styles.uploadIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
              </svg>
            </span>
            <p className={styles.uploadHint}>
              Tap to take photo or pick from gallery
            </p>
          </label>
        )}
      </div>

      {/* Signature */}
      <div className={styles.field}>
        <label className={styles.label}>
          Capture Signature <span className={styles.required}>*Required</span>
        </label>
        <canvas
          ref={canvasRef}
          className={styles.signatureCanvas}
          onMouseDown={(e) => startDraw(e.clientX, e.clientY)}
          onMouseMove={(e) => moveDraw(e.clientX, e.clientY)}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={(e) => {
            const t = e.touches[0];
            startDraw(t.clientX, t.clientY);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const t = e.touches[0];
            moveDraw(t.clientX, t.clientY);
          }}
          onTouchEnd={endDraw}
        />
        <button type="button" className={styles.clearSignatureBtn} onClick={clearSignature}>
          Clear Signature
        </button>
      </div>

      {error && <p className={styles.errorText}>{error}</p>}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.confirmBtn}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Completing…' : 'Complete Delivery'}
        </button>
        <button type="button" className={styles.cancelBtn} onClick={() => onCancel?.()} disabled={submitting}>
          Cancel
        </button>
      </div>
    </div>
  );
};
